/**
 * WCC360 v2 — how the ported v1 instrument enters the engine.
 *
 * The v1 items are self-report on a 5-point agree/disagree scale. v2 exists
 * because that measures willingness to admit things, not who someone is: "I
 * collaborate effectively" has an obvious right answer. Those items are now in
 * the flow because they carry v1's trait vocabulary and the learner-facing
 * superpower language, and because continuity with v1 reports matters.
 *
 * They must not be allowed to outrank revealed preference. So they enter
 * through exactly the same bounded channel `passions.ts` uses:
 *
 *   1. ARCHETYPE NUDGE, capped at `MAX_V1_NUDGE` points across the whole
 *      instrument — enough to break a near-tie, never enough to manufacture a
 *      primary archetype. Twenty-two behavioural decisions still decide it.
 *   2. TRAIT NARRATIVE, which is not scored at all: the trait scores and top
 *      strengths are carried to the report for explainability only.
 *
 * No eligibility gate reads any of this. A self-report answer must never
 * delete a career from anyone's pool.
 */
import { ARCHETYPES, emptyVector, type ArchetypeVector } from "../archetypes";
import {
  calculateTraitScores100,
  topTraits,
  traitCoverage,
  traitScoresToV1Archetypes,
  type TraitAnswers,
} from "./traitScoring";
import { toV2Archetypes, TRAIT_SUPERPOWERS } from "./archetypeMap";
import { weekitQuestions } from "./traitBank";
import {
  ALL_QUIZZES, mergeBoosts, quizAnswersToArchetypeBoost, type QuizAnswers,
} from "./nibble";
import { QUIZ_ANSWERS_KEY, TRAIT_ANSWERS_KEY } from "./blocks";

/**
 * Total archetype points the whole ported instrument may contribute.
 * Deliberately small, and deliberately larger than a passion pick (2) because
 * this is 92 items rather than three taps — but still under a single Part 2
 * decision, which is worth 1.0 with a 0.5 runner-up on a scale where the
 * leading archetype typically clears 6.
 */
export const MAX_V1_NUDGE = 3;

function scaleToCap(vector: ArchetypeVector, cap: number): ArchetypeVector {
  const total = ARCHETYPES.reduce((sum, a) => sum + Math.max(0, vector[a] ?? 0), 0);
  if (total <= 0) return emptyVector();
  const out = emptyVector();
  for (const a of ARCHETYPES) out[a] = (Math.max(0, vector[a] ?? 0) / total) * cap;
  return out;
}

/** Trait answers stored on the run, or an empty set. */
export function readTraitAnswers(part2: Record<string, unknown>): TraitAnswers {
  const raw = part2[TRAIT_ANSWERS_KEY];
  return raw && typeof raw === "object" ? (raw as TraitAnswers) : {};
}

/** Quiz answers stored on the run, or an empty set. */
export function readQuizAnswers(part2: Record<string, unknown>): QuizAnswers {
  const raw = part2[QUIZ_ANSWERS_KEY];
  return raw && typeof raw === "object" ? (raw as QuizAnswers) : {};
}

/**
 * The bounded archetype nudge from the ported instrument.
 *
 * Traits carry two thirds of the allowance and the three quizzes one third,
 * because the quizzes are shorter and mix stated interest with self-report.
 * A learner who answered nothing gets a zero vector, so the run is scored
 * exactly as it was before these screens existed.
 */
export function v1PortArchetypeNudge(part2: Record<string, unknown>): ArchetypeVector {
  const traitAnswers = readTraitAnswers(part2);
  const quizAnswers = readQuizAnswers(part2);

  const traitScores = calculateTraitScores100(traitAnswers);
  const traitVector = toV2Archetypes(traitScoresToV1Archetypes(traitScores));

  const boost = mergeBoosts(
    ...ALL_QUIZZES.map((q) => quizAnswersToArchetypeBoost(q, quizAnswers)),
  );
  const quizVector = toV2Archetypes(boost);

  // Partial completion scales the allowance down rather than letting six
  // answered items speak with the authority of forty-six.
  const traitShare = MAX_V1_NUDGE * (2 / 3) * traitCoverage(traitAnswers, weekitQuestions);
  const answeredQuizItems = ALL_QUIZZES.flatMap((q) => q.questions)
    .filter((q) => quizAnswers[q.id] !== undefined).length;
  const totalQuizItems = ALL_QUIZZES.reduce((n, q) => n + q.questions.length, 0);
  const quizShare = MAX_V1_NUDGE * (1 / 3) * (totalQuizItems ? answeredQuizItems / totalQuizItems : 0);

  const a = scaleToCap(traitVector, traitShare);
  const b = scaleToCap(quizVector, quizShare);
  const out = emptyVector();
  for (const k of ARCHETYPES) out[k] = (a[k] ?? 0) + (b[k] ?? 0);
  return out;
}

export interface V1TraitNarrative {
  /** Trait → 0–100. Only answered traits appear. */
  scores: Record<string, number>;
  /** Strongest traits first, with v1's superpower line for each. */
  strengths: Array<{ trait: string; score: number; superpower: string }>;
  /** 0..1 — how much of the instrument was answered. */
  coverage: number;
}

/**
 * Report-facing only. Never read by scoring, never read by eligibility.
 * Returns null when the learner answered none of the trait items, so surfaces
 * can hide the section instead of rendering an empty one.
 */
export function v1TraitNarrative(part2: Record<string, unknown>): V1TraitNarrative | null {
  const answers = readTraitAnswers(part2);
  const scores = calculateTraitScores100(answers);
  if (Object.keys(scores).length === 0) return null;
  return {
    scores,
    strengths: topTraits(scores, 5).map(({ trait, score }) => ({
      trait,
      score,
      superpower: TRAIT_SUPERPOWERS[trait] ?? "",
    })),
    coverage: traitCoverage(answers, weekitQuestions),
  };
}
